import React, { FC, ReactElement } from "react";

import { styled, Theme, CSSObject } from "@mui/material/styles";
import MuiDrawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";

import types_data from "../../data/types";

import { useAppDispatch, useAppSelector } from "../../hooks/reduxhook";
import { addType, getType } from "../../redux/slices/typeSlice";
import {
  addImageModels,
  addModel,
  getModelDetails,
} from "../../redux/slices/modelSlice";
import {
  COLLECTION,
  DEFAULT_LLM_MODEL,
  IMAGE,
  EXCEL,
} from "../../data/constants";
import type_Interface from "../../interfaces/chatTypeInterface";
import { updateIndexes } from "../../redux/slices/conversationSlice";
import { Typography, useTheme } from "@mui/material";
import {
  sendingPiiInfo,
  hidePageContentToggle,
} from "../../redux/slices/userSlice";
import { getDefaultModel } from "../LeftSidebar/NewConversationButton";

const drawerWidth = 240;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  // width: 0,
  width: `calc(${theme.spacing(8)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    // width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
    width: 0,
  }),
}));

const DrawerRight: FC<any> = ({ open, setOpen }): ReactElement => {
  const handleDrawerClose = () => {
    setOpen();
  };

  const dispatch = useAppDispatch();

  const models = useAppSelector(getModelDetails);

  const handleChatTypeSelect = (type: type_Interface) => {
    const userSelectedModel = models.model;

    if (!type.disabled) {
      dispatch(addType(type));
      dispatch(updateIndexes([]));
      handleDrawerClose();
      dispatch(sendingPiiInfo(false));
      dispatch(hidePageContentToggle(false));
      dispatch(
        addModel(
          process.env[
            `REACT_APP_DEFAULT_${type?.shortname?.toUpperCase()}_MODEL`
          ] ??
            getDefaultModel(
              models,
              DEFAULT_LLM_MODEL,
              type?.mode[0],
              userSelectedModel,
            ),
        ),
      );
      if (type.name == COLLECTION)
        dispatch(
          addImageModels(
            process.env?.REACT_APP_DEFAULT_COLLECTION_IMAGE_MODEL ??
              getDefaultModel(
                models,
                DEFAULT_LLM_MODEL,
                type?.mode[0],
                userSelectedModel,
              ),
          ),
        );
      if (type.name == IMAGE)
        dispatch(
          addModel(
            process.env?.REACT_APP_DEFAULT_IMAGE_MODEL ??
              getDefaultModel(
                models,
                DEFAULT_LLM_MODEL,
                type?.mode[1],
                userSelectedModel,
              ),
          ),
        );
    }
  };

  const type_selected = useAppSelector(getType);
  const theme = useTheme();
  return (
    <>
      <Drawer
        variant="permanent"
        open={open}
        anchor="right"
        sx={{
          display: {
            // xl: "none",
          },
        }}
      >
        <DrawerHeader
          sx={{
            background: "#231e28",
          }}
        ></DrawerHeader>
        <Divider />
        <List>
          {types_data?.map((type, index) => {
            const Component = type?.icon;
            const keywords = ["VANILLA LLM", "DOCS", "IMAGE", "COLLECTIONS"];
            const containsConfigKeywords = keywords.some((keyword) =>
              type?.name.includes(keyword),
            );

            const isReactIcon = type.name === EXCEL;
            const iconColor =
              type.name === EXCEL
                ? "rgb(253, 95, 7)"
                : containsConfigKeywords && theme.palette.mode === "dark"
                  ? "#f1f1f1"
                  : "#212121";

            return (
              <ListItem
                id={`list-item-${index}`}
                key={index}
                disablePadding
                title={type?.tooltip}
                sx={{
                  display: "block",
                  borderLeft:
                    type.name === type_selected.type
                      ? `5px solid ${
                          type.name === "VANILLA LLM" || type.name === "Neo4j"
                            ? theme.palette.mode === "dark"
                              ? "#666"
                              : "#000"
                            : type.color
                        }`
                      : "5px solid" + +theme.palette.mode === "dark"
                        ? "#666"
                        : "#fff",
                  // pointerEvents: type.disabled ? "none" : "all",
                }}
                onClick={() => handleChatTypeSelect(type)}
                disabled={type.disabled}
              >
                <ListItemButton
                  id={`list-item-${index}`}
                  sx={{
                    minHeight: 48,
                    justifyContent: open ? "initial" : "center",
                    px: 2.5,
                    py: 2,
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 3 : "auto",
                      justifyContent: "center",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    {isReactIcon
                      ? React.createElement(Component as any, {
                          style: {
                            color: iconColor,
                          },
                          size: 25,
                        })
                      : React.createElement(Component as any, {
                          style: {
                            color: iconColor,
                          },
                          width: 25,
                          height: 25,
                        })}
                    {!open && (
                      <Typography
                        sx={{
                          fontSize: "10px",
                          color: type.color,
                          textTransform: "uppercase",
                          mt: 0.2,
                        }}
                      >
                        {type.shortname}
                      </Typography>
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={type.name}
                    sx={{ opacity: open ? 1 : 0 }}
                  />
                </ListItemButton>
                <Divider />
              </ListItem>
            );
          })}
        </List>
      </Drawer>
    </>
  );
};

export default DrawerRight;
